import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { apiJson, ApiError } from '@/lib/api';
import type { AdminUserDetailResponse } from '@/lib/types';

type ExportResult = { success: boolean; message: string };

type Recipient = 'on_file' | 'custom';

function parseDetail(err: unknown): string {
  if (err instanceof ApiError && err.bodyText) {
    try {
      const j = JSON.parse(err.bodyText) as { detail?: string };
      if (j.detail) return j.detail;
    } catch {
      /* ignore */
    }
    return err.bodyText;
  }
  return err instanceof Error ? err.message : 'Request failed';
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function AdminUserExportsPage() {
  const { userId } = Route.useParams();
  const id = Number(userId);
  const { getToken } = useAuth();

  const userQuery = useQuery({
    queryKey: ['admin', 'user-detail', id],
    queryFn: () => apiJson<AdminUserDetailResponse>(getToken, `/v1/admin/users/${id}`),
  });

  const detail = userQuery.data;
  const emailOnFile = detail?.email?.trim() || null;

  const [recipient, setRecipient] = useState<Recipient>('on_file');
  const [customEmail, setCustomEmail] = useState('');
  const [includeMessages, setIncludeMessages] = useState(true);
  const [includeAccounting, setIncludeAccounting] = useState(true);
  const [includeCalendar, setIncludeCalendar] = useState(true);
  const [includeCheckIns, setIncludeCheckIns] = useState(true);
  const [includeExchange, setIncludeExchange] = useState(true);
  const [includeJournal, setIncludeJournal] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [banner, setBanner] = useState<{ ok: boolean; text: string } | null>(null);

  const resolvedEmail = useMemo(() => {
    if (recipient === 'on_file') return emailOnFile;
    return customEmail.trim() || null;
  }, [recipient, emailOnFile, customEmail]);

  const sectionsSelected =
    includeMessages ||
    includeAccounting ||
    includeCalendar ||
    includeCheckIns ||
    includeExchange ||
    includeJournal;

  const recipientValid =
    recipient === 'on_file'
      ? !!emailOnFile && EMAIL_RE.test(emailOnFile)
      : EMAIL_RE.test(customEmail.trim());

  const exportMutation = useMutation({
    mutationFn: () =>
      apiJson<ExportResult>(getToken, '/v1/admin/export-user-data', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: id,
          email: resolvedEmail,
          include_messages: includeMessages,
          include_accounting: includeAccounting,
          include_calendar: includeCalendar,
          include_check_ins: includeCheckIns,
          include_exchange_compliance: includeExchange,
          include_journal: includeJournal,
          start_date: startDate.trim() || null,
          end_date: endDate.trim() || null,
        }),
      }),
    onSuccess: (data) => setBanner({ ok: data.success, text: data.message }),
    onError: (e) => setBanner({ ok: false, text: parseDetail(e) }),
  });

  if (!detail) {
    return (
      <div className='flex justify-center py-12'>
        <div className='animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full' />
      </div>
    );
  }

  const sections: Array<[string, boolean, (v: boolean) => void]> = [
    ['Messages', includeMessages, setIncludeMessages],
    ['Accounting', includeAccounting, setIncludeAccounting],
    ['Calendar', includeCalendar, setIncludeCalendar],
    ['Check-ins', includeCheckIns, setIncludeCheckIns],
    ['Exchange compliance', includeExchange, setIncludeExchange],
    ['Journal', includeJournal, setIncludeJournal],
  ];

  return (
    <div className='space-y-6'>
      <h2 className='text-lg font-semibold text-gray-900'>Exports</h2>

      {banner ? (
        <div
          className={`rounded-lg border px-4 py-3 text-sm ${
            banner.ok
              ? 'bg-green-50 border-green-200 text-green-900'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}
        >
          {banner.text}
        </div>
      ) : null}

      <div className='bg-white rounded-lg border border-gray-200 p-4 space-y-4'>
        <div>
          <h3 className='font-semibold text-gray-900'>Email export</h3>
          <p className='text-xs text-gray-500 mt-1'>
            Generates the same court-ready packet a user gets from the in-app
            export, then emails it to the address you choose.
          </p>
        </div>

        <div className='space-y-2'>
          <p className='text-sm font-semibold text-gray-700'>Send to</p>
          <label className='flex items-start gap-2 text-sm text-gray-800'>
            <input
              type='radio'
              name='recipient'
              className='mt-1'
              checked={recipient === 'on_file'}
              onChange={() => setRecipient('on_file')}
              disabled={!emailOnFile}
            />
            <span>
              <span className='block'>Email on file</span>
              <span className='block text-xs text-gray-500'>
                {emailOnFile ?? 'User has no email on file'}
              </span>
            </span>
          </label>
          <label className='flex items-start gap-2 text-sm text-gray-800'>
            <input
              type='radio'
              name='recipient'
              className='mt-1'
              checked={recipient === 'custom'}
              onChange={() => setRecipient('custom')}
            />
            <span className='flex-1'>
              <span className='block'>Custom email</span>
              <input
                type='email'
                value={customEmail}
                onChange={(e) => setCustomEmail(e.target.value)}
                onFocus={() => setRecipient('custom')}
                placeholder='name@example.com'
                className='mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm'
              />
            </span>
          </label>
        </div>

        <div className='space-y-2'>
          <p className='text-sm font-semibold text-gray-700'>Include</p>
          <div className='grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm'>
            {sections.map(([label, checked, set]) => (
              <label key={label} className='flex items-center gap-2'>
                <input
                  type='checkbox'
                  checked={checked}
                  onChange={(e) => set(e.target.checked)}
                />
                {label}
              </label>
            ))}
          </div>
        </div>

        <div className='space-y-2'>
          <p className='text-sm font-semibold text-gray-700'>
            Date range <span className='text-xs font-normal text-gray-500'>(optional)</span>
          </p>
          <div className='flex flex-wrap gap-3'>
            <label className='text-xs text-gray-600 flex flex-col'>
              Start
              <input
                type='date'
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className='mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm'
              />
            </label>
            <label className='text-xs text-gray-600 flex flex-col'>
              End
              <input
                type='date'
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className='mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm'
              />
            </label>
          </div>
        </div>

        <div className='flex flex-wrap items-center gap-3 pt-2 border-t border-gray-100'>
          <button
            type='button'
            disabled={
              exportMutation.isPending || !sectionsSelected || !recipientValid
            }
            onClick={() => exportMutation.mutate()}
            className='px-4 py-2 text-sm font-semibold bg-primary text-white rounded-md disabled:opacity-50'
          >
            {exportMutation.isPending ? 'Queuing…' : 'Send export'}
          </button>
          {resolvedEmail ? (
            <span className='text-xs text-gray-500'>
              Will email to <strong>{resolvedEmail}</strong>
            </span>
          ) : (
            <span className='text-xs text-amber-700'>
              Choose a valid recipient to enable sending.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/admin/users/$userId/exports')({
  validateSearch: (search: Record<string, unknown>) => ({
    hid: typeof search.hid === 'string' ? search.hid : undefined,
  }),
  component: AdminUserExportsPage,
});
